import { useState } from "react";
import { toast } from "sonner";
import { portalApi } from "@/lib/portalApi";
import { formatApiErrorDetail } from "@/lib/api";

// Generic "ask us to hold this" dialog for slabs, remnants and sinks.
export const PortalReserveDialog = ({ item, defaultNote = "", defaultProject = "", onClose, onDone }) => {
  const [note, setNote] = useState(defaultNote);
  const [project, setProject] = useState(defaultProject);
  const [busy, setBusy] = useState(false);

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await portalApi.post("/inventory/reserve", {
        id: item.id,
        kind: item.kind,
        project_name: project.trim(),
        note: note.trim(),
      });
      toast.success(`Held for you — reference ${data.tag}`);
      onDone?.();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not send that request",
      );
    }
    setBusy(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/50 p-4 backdrop-blur-sm sm:items-center"
      data-testid="portal-reserve-dialog"
    >
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6">
        <div className="font-mono-data text-[10px] uppercase tracking-[0.22em] text-slate-500">
          {item.kind}
        </div>
        <h2 className="mt-1 text-xl font-bold tracking-tight text-slate-900">
          Hold {item.label || "this piece"}
        </h2>
        <p className="mt-2 text-sm text-slate-500">
          We will put it aside and come back to you to confirm. Nothing is charged
          now.
        </p>
        <input
          data-testid="portal-reserve-project"
          value={project}
          onChange={(e) => setProject(e.target.value)}
          placeholder="What is it for? (kitchen, vanity…)"
          className="mt-4 h-12 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm text-slate-900 outline-none transition-colors duration-150 focus:border-[#c39e6d]"
        />
        <textarea
          data-testid="portal-reserve-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          placeholder="Anything we should know?"
          className="mt-2 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 outline-none transition-colors duration-150 focus:border-[#c39e6d]"
        />
        <div className="mt-5 flex gap-2">
          <button
            type="button"
            data-testid="portal-reserve-cancel"
            onClick={onClose}
            className="h-12 flex-1 rounded-lg border border-slate-200 text-[11px] font-bold uppercase tracking-widest text-slate-500 transition-colors duration-150 hover:text-slate-900"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="portal-reserve-confirm"
            disabled={busy}
            onClick={send}
            className="h-12 flex-1 rounded-lg bg-[#c39e6d] text-[11px] font-bold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-[#a9855a] disabled:opacity-40"
          >
            {busy ? "Sending…" : "Reserve it"}
          </button>
        </div>
      </div>
    </div>
  );
};
